import React, { useState, useEffect } from 'react';
import { Box, Container, CssBaseline, ThemeProvider, createTheme, Stack, Button, CircularProgress, Typography, IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import Header from './components/Header';
import FloorSelector from './components/FloorSelector';
import FloorContent from './components/FloorContent';
import CustomerInfo from './components/CustomerInfo';
import SubmissionReport from './components/SubmissionReport';
import { Room, Item, Etage, RoomItem } from './types';
import { cacheService } from '../functions/services/cacheService';
import './styles/print.css';
import ErrorIcon from '@mui/icons-material/Error';
import CloudOffIcon from '@mui/icons-material/CloudOff';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

interface FloorSelections {
  selectedRooms: string[];
  roomDetails: Record<string, { customName?: string; notes?: string }>;
  items: { itemId: string; quantity: number; roomId: string; isCustom?: boolean; unitPrice?: number }[];
}

function App() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [etages, setEtages] = useState<Etage[]>([]);
  const [selectedFloor, setSelectedFloor] = useState<string>('');
  const [floorSelections, setFloorSelections] = useState<Record<string, FloorSelections>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<{ clientName: string; notes: string; date: string }>({ 
    clientName: '', 
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [showReport, setShowReport] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number>(0);

  const initializeFloorSelections = (etages: Etage[]) => {
    // Initialize all floors at once
    const newFloorSelections: Record<string, FloorSelections> = {};
    
    etages.forEach(etage => {
      newFloorSelections[etage.id] = {
        selectedRooms: [],
        roomDetails: {},
        items: []
      };
    });

    setFloorSelections(newFloorSelections);
    
    // Set the main floor (Rez-de-chaussée) as selected by default
    const mainFloor = etages.find(etage => 
      etage.name.toLowerCase().includes('rez-de-chaussée')
    );
    
    if (mainFloor) {
      setSelectedFloor(mainFloor.id);
    } else if (etages.length > 0) {
      setSelectedFloor(etages[0].id);
    }
  };

  const fetchData = async (sync = false) => {
    try {
      // Reset syncing state if it's been stuck for more than 5 minutes
      if (cacheService.isSyncing()) {
        const lastSync = cacheService.getCacheTimestamp();
        if (Date.now() - lastSync > 5 * 60 * 1000) { // 5 minutes
          console.log('Resetting stuck sync state');
          cacheService.setSyncing(false);
        }
      }

      // If not syncing and we have cache, use it
      if (!sync && cacheService.hasCache()) {
        const cachedRooms = cacheService.getRooms();
        const cachedItems = cacheService.getItems();
        const cachedEtages = cacheService.getEtages();
        const cachedRoomItems = cacheService.getRoomItems();
        if (cachedRooms.length > 0 || cachedItems.length > 0 || cachedEtages.length > 0 || cachedRoomItems.length > 0) {
          console.log('Using cached data');
          setRooms(cachedRooms);
          setEtages(cachedEtages);
          setLastUpdated(cacheService.getCacheTimestamp());
          initializeFloorSelections(cachedEtages);
          setLoading(false);
          return;
        }
      }

      // If syncing or no cache, fetch from API
      if (sync) {
        cacheService.setSyncing(true);
      }
      
      // Fetch all data types in parallel
      const [roomsResponse, itemsResponse, etagesResponse, roomItemsResponse] = await Promise.all([
        fetch('/api/rooms'),
        fetch('/api/items'),
        fetch('/api/etages'),
        fetch('/api/room-items')
      ]);

      if (!roomsResponse.ok || !itemsResponse.ok || !etagesResponse.ok || !roomItemsResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const [roomsData, itemsData, etagesData, roomItemsData] = await Promise.all([
        roomsResponse.json() as Promise<Room[]>,
        itemsResponse.json() as Promise<Item[]>,
        etagesResponse.json() as Promise<Etage[]>,
        roomItemsResponse.json() as Promise<RoomItem[]>
      ]);
      
      // Save all data to cache
      cacheService.saveToCache({ 
        rooms: roomsData,
        items: itemsData,
        etages: etagesData,
        roomItems: roomItemsData
      }, 'api');
      setLastUpdated(Date.now());
      
      setRooms(roomsData);
      setEtages(etagesData);
      initializeFloorSelections(etagesData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      cacheService.setSyncError(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
      cacheService.setSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSync = async () => {
    setRefreshing(true);
    await fetchData(true);
  };

  const handleFloorChange = (floorId: string) => {
    if (!floorId) return;
    
    setSelectedFloor(floorId);
    // No need to initialize floor selection here as it's already done in initializeFloorSelections
  };

  const handleRoomChange = (floorId: string, roomIds: string[], details: Record<string, { customName?: string; notes?: string }>) => {
    setFloorSelections(prev => ({
      ...prev,
      [floorId]: {
        ...prev[floorId],
        selectedRooms: roomIds,
        roomDetails: details
      }
    }));
  };

  const handleItemsChange = (floorId: string, items: { itemId: string; quantity: number; roomId: string; isCustom?: boolean; unitPrice?: number }[]) => {
    console.log('App: handleItemsChange called with:', { floorId, items });
    
    setFloorSelections(prev => {
      // Get the current floor's data
      const currentFloor = prev[floorId] || { selectedRooms: [], roomDetails: {}, items: [] };
      
      // Update the items while preserving other floor data
      const updated = {
        ...prev,
        [floorId]: {
          ...currentFloor,
          items: items // Keep all items without filtering
        }
      };
      
      console.log('App: Updated floor selections:', updated);
      return updated;
    });
  };

  const handleSubmit = () => {
    console.log('App: Submitting with floor selections:', floorSelections);
    console.log('App: Submitting:', {
      clientName: formData.clientName,
      notes: formData.notes,
      date: formData.date,
      floors: Object.entries(floorSelections).map(([floorId, data]) => ({
        floorId,
        selectedRooms: data.selectedRooms,
        roomDetails: data.roomDetails,
        items: data.items
      }))
    });
    setShowReport(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleFormDataChange = (newFormData: { clientName: string; notes?: string; date: string }) => {
    setFormData(prev => ({
      ...prev,
      clientName: newFormData.clientName,
      notes: newFormData.notes || '',
      date: newFormData.date
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header />
        <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
          <Stack spacing={4}>
            {!showReport ? (
              <>
                <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    {lastUpdated > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {cacheService.getDataSource() === 'cache' ? (
                          <Tooltip title="Données issues du cache">
                            <CloudOffIcon color="action" fontSize="small" />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Données issues du serveur">
                            <SyncIcon color="primary" fontSize="small" />
                          </Tooltip>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Dernière synchronisation : {new Date(lastUpdated).toLocaleString('fr-FR')}
                        </Typography>
                      </Box>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SyncIcon />}
                      onClick={handleSync}
                      disabled={refreshing || cacheService.isSyncing()}
                      sx={{ minWidth: 150, fontWeight: 600 }}
                    >
                      Synchroniser
                    </Button>
                  </Stack>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <CustomerInfo
                    formData={formData}
                    onFormDataChange={handleFormDataChange}
                  />
                </Box>
                {cacheService.getLastSyncError() && (
                  <Alert 
                    severity="error" 
                    onClose={() => cacheService.setSyncError('')}
                    icon={<ErrorIcon />}
                  >
                    Sync failed: {cacheService.getLastSyncError()}
                  </Alert>
                )}
                <Box sx={{
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  boxShadow: 1,
                  mb: 4,
                  p: { xs: 1, sm: 2, md: 3 }
                }}>
                  <FloorSelector
                    selectedFloor={selectedFloor}
                    onFloorChange={handleFloorChange}
                  />
                  <FloorContent
                    floorId={selectedFloor}
                    rooms={rooms}
                    selectedRooms={floorSelections[selectedFloor].selectedRooms}
                    roomDetails={floorSelections[selectedFloor].roomDetails}
                    onRoomChange={(roomIds, details) => handleRoomChange(selectedFloor, roomIds, details)}
                    onItemsChange={(items) => handleItemsChange(selectedFloor, items)}
                    onSubmit={handleSubmit}
                  />
                </Box>
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSubmit}
                    sx={{ minWidth: 200 }}
                  >
                    Soumettre
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowReport(false)}
                  >
                    Retour au formulaire
                  </Button>
                </Box>
                <SubmissionReport
                  formData={formData}
                  floorSelections={floorSelections}
                  rooms={rooms}
                  etages={etages}
                />
              </>
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App; 